import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { defer, finalize, tap } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { SystemService } from '../../../core/admin/system.service';
import {
  ClaudeTestResult,
  EmailTemplateOption,
  SeedDemoResult,
  SystemStatus,
  TestEmailResult,
} from '../../../core/admin/system.types';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../shared/confirm-dialog/confirm-dialog';
import { IconComponent } from '../../../shared/icon/icon';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';

@Component({
  selector: 'app-system-status',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    RouterLink,
    IconComponent,
    StatusChipComponent,
  ],
  templateUrl: './system-status.html',
  styleUrl: './system-status.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemStatusComponent implements OnInit {
  private readonly systemService = inject(SystemService);
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  // The S3 check performs a real read/write round-trip, so it can be slow.
  protected readonly systemStatus = signal<SystemStatus | null>(null);
  protected readonly statusLoading = signal(true);
  protected readonly statusError = signal(false);

  // Test-email tooling.
  protected readonly templates = signal<EmailTemplateOption[]>([]);
  protected readonly sending = signal(false);
  protected readonly sendResult = signal<TestEmailResult | null>(null);
  protected readonly sendError = signal<string | null>(null);

  // Claude API connection test (a live, billable call — runs only on demand).
  protected readonly claudeTesting = signal(false);
  protected readonly claudeResult = signal<ClaudeTestResult | null>(null);
  protected readonly claudeError = signal<string | null>(null);

  // NEAR AI connection test, also a live, billable call on demand.
  protected readonly nearTesting = signal(false);
  protected readonly nearResult = signal<ClaudeTestResult | null>(null);
  protected readonly nearError = signal<string | null>(null);

  // Demo seeding (FLOW-181) — a write action, confirmed before it runs.
  protected readonly seeding = signal(false);
  // No `seedError` counterpart to the Claude action: a failed request keeps the
  // dialog open with the message inside it (§4b.1), so the card only ever shows
  // a result the server actually returned.
  protected readonly seedResult = signal<SeedDemoResult | null>(null);

  protected readonly emailForm = this.fb.nonNullable.group({
    recipient: [
      this.authService.currentUser()?.email ?? '',
      [Validators.required, Validators.email],
    ],
    template: ['', Validators.required],
  });

  ngOnInit(): void {
    this.loadSystemStatus();
    this.loadTemplates();
  }

  protected loadSystemStatus(): void {
    this.statusLoading.set(true);
    this.statusError.set(false);
    this.cdr.markForCheck();

    this.systemService
      .getSystemStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status) => {
          this.systemStatus.set(status);
          this.statusLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.statusLoading.set(false);
          this.statusError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  private loadTemplates(): void {
    this.systemService
      .getTestEmailTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.templates.set(res.templates);
          if (res.templates.length > 0) {
            this.emailForm.controls.template.setValue(res.templates[0].key);
          }
          this.cdr.markForCheck();
        },
        // Non-fatal: the page still works without the test-email tool.
        error: () => this.cdr.markForCheck(),
      });
  }

  protected sendTestEmail(): void {
    if (this.emailForm.invalid || this.sending()) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const { recipient, template } = this.emailForm.getRawValue();
    this.sending.set(true);
    this.sendResult.set(null);
    this.sendError.set(null);
    this.cdr.markForCheck();

    this.systemService
      .sendTestEmail(template, recipient)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.sendResult.set(result);
          this.sending.set(false);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.sendError.set(
            err.error?.detail ?? 'Could not send the test email. Please try again.',
          );
          this.sending.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected testClaudeConnection(): void {
    if (this.claudeTesting()) {
      return;
    }

    this.claudeTesting.set(true);
    this.claudeResult.set(null);
    this.claudeError.set(null);
    this.cdr.markForCheck();

    this.systemService
      .testClaudeConnection()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.claudeResult.set(result);
          this.claudeTesting.set(false);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.claudeError.set(
            err.error?.detail ?? 'Could not reach the Claude API. Please try again.',
          );
          this.claudeTesting.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected testNearConnection(): void {
    if (this.nearTesting()) {
      return;
    }

    this.nearTesting.set(true);
    this.nearResult.set(null);
    this.nearError.set(null);
    this.cdr.markForCheck();

    this.systemService
      .testNearConnection()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.nearResult.set(result);
          this.nearTesting.set(false);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.nearError.set(err.error?.detail ?? 'Could not reach NEAR AI. Please try again.');
          this.nearTesting.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Populate this environment with demo clients, applications and documents.
   *
   * Confirmed first because it writes: the environments that accept it are
   * shared, and seeding uploads real documents that can trigger billable AI
   * checks. A refusal (production, or staging with SEEDING_ENABLED off) arrives
   * as a 200 with `status: 'error'`, so it renders in the result block rather
   * than as a failure inside the dialog.
   */
  protected seedDemoData(): void {
    if (this.seeding()) {
      return;
    }

    const data: ConfirmDialogData = {
      title: 'Seed demo data?',
      message:
        'This creates demo clients, applications and facilities in this ' +
        'environment, and uploads the example PDFs. If the document-assessment ' +
        'feature is switched on, each uploaded document triggers a live, ' +
        'billable Claude check. Nothing existing is deleted.',
      confirmLabel: 'Seed demo data',
      // `defer` so the busy state flips when Confirm is pressed, not when the
      // dialog opens.
      confirm: () =>
        defer(() => {
          this.seeding.set(true);
          this.seedResult.set(null);
          this.cdr.markForCheck();
          return this.systemService.seedDemoData();
        }).pipe(
          tap((result) => this.seedResult.set(result)),
          finalize(() => {
            this.seeding.set(false);
            this.cdr.markForCheck();
          }),
        ),
      errorMessage: (err: unknown) =>
        (err as HttpErrorResponse)?.error?.detail ?? 'Could not seed demo data. Please try again.',
    };

    this.dialog.open(ConfirmDialogComponent, { width: '440px', data });
  }
}
